'use strict';

import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Status, TrackingConstants, fetchUpdatedFilterState, updateLocation, updatePostcode } from '../cfs-search-slice';

import user from '../../../../profile/user';
import analytics from '../../../../utils/analytics';
import postcodeFormatting from '../../../../utils/postcode-formatting';

export const LocationFilter = () => {
	const defaultValue = '2000';
	const dispatch = useDispatch();

	const distance = useSelector(state => state.cfsSearch.filterState.distance) ?? defaultValue;
	const postcode = useSelector(state => state.cfsSearch.location.postcode);
	const latLong = useSelector(state => state.cfsSearch.location.latLong);
	const invalid = useSelector(state => state.cfsSearch.location.invalid);
	const filterStatus = useSelector((state) => state.cfsSearch.filterStatus);

	const [postcodeAltered, setPostcodeAltered] = useState(false);
	const [postcodeInvalid, setPostcodeInvalid] = useState(false);

	useEffect(() => {
		if (invalid) {
			document.getElementById('offCanvasSearch').scrollTo(0, 0);
		}
	}, [invalid]);

	const onPostcodeChange = (e) => {
		const newPostcode = e.target.value;
		dispatch(updatePostcode(newPostcode));
		setPostcodeAltered(true);

		const isValid = postcodeFormatting.isValid(newPostcode);
		setPostcodeInvalid(!isValid);
	};

	const onPostcodeBlur = () => {
		if (postcodeAltered && !postcodeInvalid) {
			user.setPostcode(postcode);
			analytics.trackEvent(TrackingConstants.Categories.Added, 'Postcode', 'Postcode entered');

			dispatch(fetchUpdatedFilterState({
				name: 'postcode',
				value: postcode
			}));
		}

		setPostcodeAltered(false);
	};

	const onPostcodeKeyPress = (e) => {
		if (filterStatus !== Status.Loading && (e.charCode === 13 || e.key === 'Enter')) {
			// enter key pressed
			e.preventDefault();
			e.stopPropagation();

			onPostcodeBlur();
		}
	};

	const onDistanceChange = (e) => {
		const value = e.target.value;

		analytics.trackEvent(TrackingConstants.Categories.Added, 'Distance', value);

		dispatch(fetchUpdatedFilterState({
			name: 'distance',
			value: value
		}));
	}

	const onLocationToggle = (e) => {
		e.preventDefault();
		setPostcodeInvalid(false);

		if (latLong) {
			user.setLocation(null);
			analytics.trackEvent(TrackingConstants.Categories.Removed, 'Location', 'Location removed');

			dispatch(updateLocation(null));

			dispatch(fetchUpdatedFilterState({
				name: 'latLong',
				value: null
			}));

			return;
		}

		navigator.geolocation.getCurrentPosition(
			function (position) {
				const newLatLong = {
					'latitude': position.coords.latitude,
					'longitude': position.coords.longitude
				};

				user.setLocation(newLatLong);
				analytics.trackEvent(TrackingConstants.Categories.Added, 'Location', 'Location used');

				dispatch(updateLocation(newLatLong));

				dispatch(fetchUpdatedFilterState({
					name: 'latLong',
					value: newLatLong
				}));
			},

			function (error) {
				alert(error.message);
			}
		);
	};

	const filterClassName = invalid || (postcodeInvalid && !postcodeAltered)
		? 'search-filters__location search-filters__location--invalid'
		: 'search-filters__location';

	const locationIconClassName = latLong
		? 'search-filters__location__use-location__icon icon-12x12-tick'
		: 'search-filters__location__use-location__icon icon-12x12-target';

	const isPostcodeDisabled = filterStatus === Status.Loading || latLong;

	const postcodeValue = latLong ? '' : postcode;
	const postcodePlaceholder = latLong ? 'Using my location' : 'Enter postcode...';

	const headingText = postcodeInvalid && !postcodeAltered
		? 'This postcode is invalid'
		: 'Provide your postcode or location';

	return (
		<div className={filterClassName}>
			<h3 className="search-filters__location__heading">{headingText}</h3>
			<div className="search-filters__location__selection-row clearfix">
				<input className="search-filters__location__postcode"
					type="text"
					placeholder={postcodePlaceholder}
					value={postcodeValue}
					onBlur={onPostcodeBlur}
					onKeyPress={onPostcodeKeyPress}
					onChange={onPostcodeChange}
					disabled={isPostcodeDisabled}
				/>

				<a className="search-filters__location__use-location" onClick={onLocationToggle}>
					<span className={locationIconClassName}></span>
					<span className="search-filters__location__use-location__text">Use my location</span>
				</a>
			</div>

			<label>Distance:</label>
			<select value={distance} onChange={onDistanceChange}>
				<option key={defaultValue} value={defaultValue}>Any distance</option>
				<option key="1" value="1">Within 1 mile</option>
				<option key="5" value="5">Within 5 miles</option>
				<option key="10" value="10">Within 10 miles</option>
				<option key="20" value="20">Within 20 miles</option>
				<option key="30" value="30">Within 30 miles</option>
				<option key="40" value="40">Within 40 miles</option>
				<option key="60" value="60">Within 60 miles</option>
				<option key="100" value="100">Within 100 miles</option>
				<option key="200" value="200">Within 200 miles</option>
			</select>
		</div>
	);
}